/**
 * The order a society's courses are offered in when setting up a round.
 *
 * Alphabetical is the order of a directory, not of a decision. A society plays
 * the same two or three courses most of the time, and those were as likely to
 * be at the bottom of an A-to-Z as the top — so setting up the round everybody
 * plays every month began with scrolling and searching for it.
 *
 * So: the ones this society actually plays, most-played first, then everything
 * else alphabetically. `play_count` and `last_played` come from `/courses`,
 * where they are counted in DAYS rather than cards (a forty-player outing is
 * one visit, not forty).
 *
 * `last_played` breaks a tie: two courses played four times each are not
 * equally likely, and the recent one is the better guess. Name breaks that, so
 * the list is stable rather than arbitrary when a society is new and nothing
 * has been played at all.
 */

/** How many of the top courses are worth calling out as a group. */
export const FAVOURITES_SHOWN = 3;

/** True where this society has history with the course. */
export const isPlayed = (course) => (course?.play_count || 0) > 0;

export function bySocietyFavour(a, b) {
  const byCount = (b.play_count || 0) - (a.play_count || 0);
  if (byCount) return byCount;
  // Dates are ISO strings from the server, so string order IS date order.
  const byRecency = String(b.last_played || '').localeCompare(String(a.last_played || ''));
  if (byRecency) return byRecency;
  return String(a.name || '').localeCompare(String(b.name || ''));
}

/** The whole list, in the order a setup screen should offer it. */
export const orderCourses = (courses) => [...(courses || [])].sort(bySocietyFavour);

/**
 * The line under a course in the list — why it is where it is.
 *
 * Only ever says what it knows: a course with no history gets nothing rather
 * than "0 times", which would read as a warning against playing there.
 */
export function playedLabel(course) {
  const n = course?.play_count || 0;
  if (!n) return '';
  return n === 1 ? 'Played once' : `Played ${n} times`;
}
